import { useEffect, useMemo, useState } from 'react';
import { LawfulPresenceAttestation, LawfulPresenceDisplayMode } from 'src/data';
import { showsStatusOptions } from 'src/utils';

export interface Applicant {
  /** Server id, carried back on save so the member is updated instead of duplicated. */
  id?: string;
  age: number;
  relation: 'SELF' | 'SPOUSE' | 'CHILD';
  sex: 'MALE' | 'FEMALE';
  lawfulPresence?: LawfulPresenceAttestation;
  checkLawfulPresence?: boolean;
}

const getBlankApplicant = (relation) => ({
  isApplying: true,
  relation,
  age: null,
  sex: null,
});

export const useApplicants = (members = [], lawfulPresenceMode?: LawfulPresenceDisplayMode) => {
  const [applicants, setApplicants] = useState([getBlankApplicant('SELF')]);

  useEffect(() => {
    if (members.length > 0) {
      setApplicants(
        members.map((m) => ({
          id: m?.id,
          isApplying: m?.isApplying,
          age: m?.age,
          relation: m?.relation,
          sex: m?.sex,
          lawfulPresence: m?.lawfulPresence ?? undefined,
          checkLawfulPresence: !!m?.lawfulPresence,
        })),
      );
    }
  }, [members]);

  const hasSpouse = useMemo(() => {
    return applicants?.some((a) => a.relation === 'SPOUSE');
  }, [applicants]);

  const isValidMembers = useMemo(() => {
    return applicants.every((a) => {
      if (!(a.sex && a.age && a.age <= 100 && a.age >= 0)) return false;

      if (showsStatusOptions(lawfulPresenceMode, a.checkLawfulPresence) && !a.lawfulPresence) {
        return false;
      }

      return true;
    });
  }, [applicants, lawfulPresenceMode]);

  const addApplicant = (relation) => {
    setApplicants([...applicants, getBlankApplicant(relation)]);
  };

  const updateApplicant = (index, value) => {
    const array = [...applicants];
    array[index] = value;
    setApplicants([...array]);
  };

  const removeApplicant = (index) => {
    const array = applicants.filter((it, i) => i !== index);
    setApplicants(array);
  };

  return {
    isValidMembers,
    hasSpouse,
    applicants,
    addApplicant,
    updateApplicant,
    removeApplicant,
    setApplicants,
  };
};
